<template>
  <div>
    <loading-spinner
      v-if="!tasks"
      color="success"
    />
    <div v-else>
      <div class="mb-3">
        <task-edition-dialog
          v-if="xs"
          mode="create"
          size="large"
          @reload="loadTasks"
        />
      </div>
      <div class="d-flex">
        <v-text-field
          v-model="search"
          name="search"
          :label="t('global.search')"
          class="me-2 mb-1"
          clearable
          @change="loadTasks"
        />
        <v-select
          v-model="selectedFilterIds"
          :items="availableFilters"
          item-value="id"
          item-title="name"
          multiple
          chips
          clearable
          class="mb-2"
          :placeholder="t('task.select_filters')"
          @update:model-value="loadTasks"
        >
          <template #item="{ props, item }">
            <v-list-item
              v-bind="props"
              :subtitle="item.description"
            />
          </template>
          <template
            v-if="!xs"
            #append
          >
            <task-edition-dialog
              mode="create"
              size="large"
              @reload="loadTasks"
            />
          </template>
        </v-select>
      </div>
      <div v-if="tasks.length > 0">
        <task-card
          v-for="task in tasks"
          :key="task.id"
          mode="index"
          :task="task"
          @reload="loadTasks"
        />
      </div>

      <div
        v-if="tasks.length > 0"
        class="overflow-auto pt-2"
      >
        <v-pagination
          v-model="currentPage"
          :total-rows="totalTaskCount"
          :length="totalPages"
          :per-page="tasksPerPage"
          :prev-text="t('global.previous')"
          :next-text="t('global.next')"
          @update:model-value="loadTasks"
        />
      </div>
      <v-alert
        v-else
        color="secondary"
        :text="t('task.none')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import axios from 'axios';
import {
  computed,
  onMounted,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';
import {
  useAuth,
  useDataTableUtilities,
  useDate,
} from '@/composables';
import { useErrorStore } from '@/stores/errors.ts';
import TaskCard from './TaskCard.vue';
import TaskEditionDialog from './TaskEditionDialog.vue';

const { xs } = useDisplay();
const {
  getDataTableUrl,
  watchString,
  search,
} = useDataTableUtilities();

const { t } = useI18n();
const { handleError } = useErrorStore();
const { unixFilter, currentUnix } = useDate();
const { authUser } = useAuth();
const tasks = ref<Array<App.Models.Task>>([]);
const taskCategories = ref<Array<App.Models.TaskCategory>>([]);
const activeStores = ref<Array<App.Models.Store>>([]);
const { watchArray, getNumberArrayFromQuery } = useDataTableUtilities();
const route = useRoute();
const totalPages = ref(1);
const tasksPerPage = ref(10);
const totalTaskCount = ref(0);
const currentPage = ref(1);
// URL-synced filter ids as CSV (?filters=1,4)
const selectedFilterIds = ref<number[]>(getNumberArrayFromQuery(route.query?.filters, []));
watchArray(selectedFilterIds, 'filters', [], true);

watchString(search, 'search', '');

const availableFilters = computed(() => {
  const filters = [
    {
      id: 1,
      name: t('task.filters.assigned_to_me'),
      description: t('task.filters.assigned_to_me_description'),
      filter: {
        column: 'assignee_id',
        operator: '=',
        value: authUser.id,
      },
    },
    {
      id: 2,
      name: t('states.assigned'),
      description: t('task.filters.assigned_description'),
      filter: {
        column: 'assignee_id',
        operator: '<>',
        value: null,
      },
    },
    {
      id: 3,
      name: t('states.unassigned'),
      description: t('task.filters.unassigned_description'),
      filter: {
        column: 'assignee_id',
        operator: '=',
        value: null,
      },
    },
    {
      id: 4,
      name: t('states.open'),
      description: t('task.filters.open_description'),
      filter: {
        column: 'status',
        operator: '<>',
        value: 'completed',
      },
    },
    {
      id: 5,
      name: t('states.completed'),
      description: t('task.filters.completed_description'),
      filter: {
        column: 'status',
        operator: '=',
        value: 'completed',
      },
    },
  ];
  if (taskCategories.value) {
    taskCategories.value.forEach((category) => {
      filters.push({
        id: (filters.length + 1),
        name: category.name,
        description: t('task.filters.category_description', { category: category.name }),
        filter: {
          column: 'task_category_id',
          operator: '=',
          value: category.id,
        },
      });
    });
  }
  if (activeStores.value) {
    activeStores.value.forEach((store) => {
      filters.push({
        id: (filters.length + 1),
        name: store.name,
        description: t('task.filters.store_description', { store: store.name }),
        filter: {
          column: 'store_id',
          operator: '=',
          value: store.id,
        },
      });
    });
  }
  return filters;
});
const activeFilters = computed<any>(() => {
  const ids = selectedFilterIds.value ?? [];
  return availableFilters.value
    .filter((f: any) => ids.includes(f.id))
    .map((f: any) => f.filter);
});

function saveSelectedFilters() {
  const ids = selectedFilterIds.value ?? [];
  localStorage.setItem('TaskFilters', JSON.stringify({
    timestamp: currentUnix(),
    filters: ids,
  }));
}

async function loadTasks() {
  const url = getDataTableUrl(
    '/api/tasks',
    [
      'store',
      'reporter',
      'assignee',
      'assignee.profileImage',
      'comments',
    ],
    tasksPerPage.value,
    currentPage.value,
    search.value,
    [],
    [
      { key: 'id', order: 'desc' },
    ],
    null,
    activeFilters.value,
  );

  saveSelectedFilters();
  try {
    const response = await axios.get(url);
    tasks.value = response.data.data;
    // Updating pagination data
    totalTaskCount.value = response.data.total;
    totalPages.value = response.data.last_page;
  }
  catch (error) {
    handleError(error, t('task.cannot_load'));
  }
}

async function loadTaskCategories() {
  try {
    const response = await axios.get('/api/taskcategories');
    taskCategories.value = response.data.data;
  }
  catch (error) {
    handleError(error, t('task.cannot_load'));
  }
}
async function loadActiveStores() {
  try {
    const response = await axios.get('/api/stores?active=true');
    activeStores.value = response.data.data;
  }
  catch (error) {
    handleError(error, t('store.cannot_load'));
  }
}
function loadSelectedFilters(): number[] {
  try {
    const storageData = localStorage.getItem('TaskFilters');
    if (storageData) {
      const filterData = JSON.parse(storageData);
      if (filterData.timestamp && unixFilter(filterData.timestamp)) {
        const stored = filterData.filters;
        if (Array.isArray(stored)) {
          if (stored.length === 0)
            return [];
          if (typeof stored[0] === 'number') {
            return stored as number[];
          }
          // Backward compatibility: array of objects with id
          return (stored as any[])
            .map((o: any) => o?.id)
            .filter((id: any) => typeof id === 'number');
        }
        return [];
      }
    }
    return [4];
  }
  catch {
    return [4];
  }
}

onMounted(() => {
  // Prefer URL-provided ids; if none, seed from saved defaults
  if ((selectedFilterIds.value?.length ?? 0) === 0) {
    selectedFilterIds.value = loadSelectedFilters();
  }

  loadTasks();
  loadTaskCategories();
  loadActiveStores();
});
</script>
